import { Activity, Clock3, Gauge, RefreshCw, ShieldCheck, Target, TrendingUp } from "lucide-react";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, postJson } from "./api";
import { useSessionStore } from "./store";

type Metric = {
  enabled: boolean;
  score_bonus: number;
  return_30d_pct: number;
  return_7d_pct: number;
  profit_factor_30d: number;
  profit_factor_7d: number;
  trade_count_30d: number;
  expectancy_30d_krw: number;
  median_trade_return_30d_pct: number;
  fee_ratio_30d_pct: number;
  avg_mdd_30d_pct: number;
  profitable_market_30d_pct: number;
  oos_fold_count: number;
  oos_trade_count: number;
  oos_return_pct: number;
  oos_profitable_fold_pct: number;
  oos_expectancy_krw: number;
  oos_profit_factor: number;
  oos_max_drawdown_pct: number;
  reason: string;
};

type RegimeMetric = {
  enabled: boolean;
  score_bonus: number;
  trade_count_30d: number;
  profit_factor_30d: number;
  expectancy_30d_krw: number;
  median_trade_return_30d_pct: number;
  oos_fold_count: number;
  oos_trade_count: number;
  oos_return_pct: number;
  oos_profitable_fold_pct: number;
  oos_expectancy_krw: number;
  oos_profit_factor: number;
  oos_max_drawdown_pct: number;
  reason: string;
};

type AdaptiveStatus = {
  enabled: boolean;
  refresh_running: boolean;
  analysis_interval_hours: number;
  switch_cooldown_hours: number;
  analysis_market_count: number;
  live_analysis_market_limit: number;
  live_analysis_mover_pool_size: number;
  policy_age_hours: number | null;
  generated_at_utc: string | null;
  next_evaluation_at_utc: string | null;
  selected_markets: string[];
  selected_market_buckets: Record<string, string>;
  strategies: Record<string, Metric>;
  regime_strategies: Record<string, Record<string, RegimeMetric>>;
};

type AdaptiveRefreshResponse = {
  status: "queued" | "already_running";
  refresh_running: boolean;
  research_only: boolean;
};

type PerformanceRow = {
  window: "7d" | "30d" | "lifetime";
  scope: "strategy" | "market" | "regime" | "market_regime";
  strategy: string;
  market: string | null;
  regime: string | null;
  trade_count: number;
  win_count: number;
  loss_count: number;
  win_rate_pct: number;
  gross_profit_krw: number;
  gross_loss_krw: number;
  net_pnl_krw: number;
  fees_krw: number;
  profit_factor: number;
  expectancy_krw: number;
  avg_return_pct: number;
  median_return_pct: number;
  avg_hold_minutes: number;
  max_consecutive_losses: number;
  payoff_ratio: number;
  generated_at: string | null;
};

type PerformanceReport = {
  source: string;
  generated_at_utc: string | null;
  windows: string[];
  rows: PerformanceRow[];
};

type ExecutionMetricBlock = {
  sample_count: number;
  median_signed_slippage_bps: number;
  median_adverse_friction_bps: number;
  p75_adverse_friction_bps: number;
  p90_adverse_friction_bps: number;
  median_first_fill_ms: number;
  p90_terminal_fill_ms: number;
  total_expected_fee_krw: number;
  total_actual_fee_krw: number;
  fee_delta_krw: number;
};

type ExecutionQualityReport = {
  configured_total_bps: number;
  minimum_observed_samples: number;
  adaptive_use_observed: boolean;
  windows: Record<"7d" | "30d", {
    days: number;
    generated_at_utc: string;
    combined: ExecutionMetricBlock;
    bid: ExecutionMetricBlock;
    ask: ExecutionMetricBlock;
  }>;
  adaptive_friction: {
    sample_count: number;
    min_samples: number;
    sufficient_samples: boolean;
    configured_total_bps: number;
    observed_total_bps: number;
    stress_total_bps: number;
    observed_source: string;
  };
};

const LABELS: Record<string, { title: string; subtitle: string }> = {
  breakout_trend: { title: "Breakout Trend", subtitle: "추세 돌파 · 우선 전략" },
  trend_pullback: { title: "Trend Pullback", subtitle: "눌림목 · 품질 필터" },
  range_reversion: { title: "Range Reversion", subtitle: "횡보 평균회귀 · 제한적" },
  scalp: { title: "Scalp", subtitle: "초단기 급상승 · 격리 전략" },
  legacy_momentum: { title: "Legacy Momentum", subtitle: "기존 모멘텀 경로" },
};

const pct = (value: number, digits = 2) => `${value > 0 ? "+" : ""}${Number(value || 0).toFixed(digits)}%`;
const pf = (value: number) => Number(value || 0).toFixed(2);
const money = (value: number) => `${value > 0 ? "+" : ""}${Math.round(Number(value || 0)).toLocaleString("ko-KR")}원`;
const minutes = (value: number) => value >= 60 ? `${(value / 60).toFixed(1)}시간` : `${Math.round(value)}분`;
const dateTime = (value: string | null) => value ? new Intl.DateTimeFormat("ko-KR", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Seoul" }).format(new Date(value)) : "정책 생성 대기";
const strategyTitle = (name: string) => LABELS[name]?.title || name;
const REGIMES = [
  ["bull", "강세"],
  ["normal", "보통"],
  ["bear", "약세"],
  ["severe_bear", "심한 약세"],
  ["extreme_bear", "극약세"],
] as const;

export default function StrategyPage() {
  const authenticated = useSessionStore((state) => state.authenticated);
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ["adaptive-strategy"],
    queryFn: () => api<AdaptiveStatus>("/api/strategy/adaptive"),
    enabled: authenticated,
    refetchInterval: 60_000,
  });
  const performanceQuery = useQuery({
    queryKey: ["strategy-performance"],
    queryFn: () => api<PerformanceReport>("/api/strategy/performance"),
    enabled: authenticated,
    refetchInterval: 60_000,
  });
  const executionQualityQuery = useQuery({
    queryKey: ["execution-quality"],
    queryFn: () => api<ExecutionQualityReport>("/api/strategy/execution-quality"),
    enabled: authenticated,
    refetchInterval: 60_000,
  });
  const [interval, setInterval] = useState<number | null>(null);
  const [poolLimit, setPoolLimit] = useState<number | null>(null);
  const [moverSlots, setMoverSlots] = useState<number | null>(null);
  const [savingPool, setSavingPool] = useState(false);
  const [performanceWindow, setPerformanceWindow] = useState<"7d" | "30d" | "lifetime">("30d");
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState("");

  if (!authenticated) {
    return <div className="page-stack"><div className="page-intro"><div><span className="eyebrow">ADAPTIVE STRATEGY</span><h2>전략 성과는 관리자에게만 표시됩니다</h2><p>상단에서 로그인하면 실제 체결 성과와 7일/30일 Adaptive 정책을 확인할 수 있습니다.</p></div></div><div className="control-lock"><ShieldCheck size={30} /><strong>관리자 세션 필요</strong><span>실거래 전략 성과와 정책 결정 정보는 보호된 운영 데이터입니다.</span></div></div>;
  }

  if (query.isLoading) return <div className="skeleton-stack">{Array.from({ length: 6 }, (_, i) => <div className="skeleton" key={i} />)}</div>;
  if (query.isError || !query.data) return <div className="empty-state"><strong>Adaptive 정책을 불러오지 못했습니다</strong><p>{query.error instanceof Error ? query.error.message : "API 상태를 확인해 주세요."}</p></div>;

  const data = query.data;
  const selectedInterval = interval ?? data.analysis_interval_hours;
  const selectedPoolLimit = poolLimit ?? data.live_analysis_market_limit;
  const selectedMoverSlots = moverSlots ?? data.live_analysis_mover_pool_size;
  const poolDirty =
    selectedPoolLimit !== data.live_analysis_market_limit ||
    selectedMoverSlots !== data.live_analysis_mover_pool_size;
  const strategies = Object.entries(data.strategies || {});
  const performanceRows = performanceQuery.data?.rows || [];
  const liveStrategies = performanceRows
    .filter((row) => row.window === performanceWindow && row.scope === "strategy")
    .sort((a, b) => b.net_pnl_krw - a.net_pnl_krw);
  const regimeRows = performanceRows
    .filter((row) => row.window === performanceWindow && row.scope === "regime")
    .sort((a, b) => b.trade_count - a.trade_count || b.net_pnl_krw - a.net_pnl_krw);
  const marketRows = performanceRows
    .filter((row) => row.window === performanceWindow && row.scope === "market")
    .sort((a, b) => b.trade_count - a.trade_count || b.net_pnl_krw - a.net_pnl_krw)
    .slice(0, 12);

  async function saveInterval() {
    setSaving(true);
    setMessage("");
    try {
      await postJson("/api/settings/strategy", { adaptive_analysis_interval_hours: selectedInterval });
      setMessage(`${selectedInterval}시간 주기로 저장했습니다.`);
      await client.invalidateQueries({ queryKey: ["adaptive-strategy"] });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "저장하지 못했습니다.");
    } finally {
      setSaving(false);
    }
  }

  async function saveAnalysisPool() {
    setSavingPool(true);
    setMessage("");
    try {
      await postJson("/api/settings/strategy", {
        analysis_market_limit: selectedPoolLimit,
        analysis_mover_pool_size: selectedMoverSlots,
      });
      setMessage(`실시간 분석 풀을 ${selectedPoolLimit}종목(상승 슬롯 ${selectedMoverSlots})으로 저장했습니다.`);
      await client.invalidateQueries({ queryKey: ["adaptive-strategy"] });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "저장하지 못했습니다.");
    } finally {
      setSavingPool(false);
    }
  }

  async function refreshAdaptivePolicy() {
    setRefreshing(true);
    setMessage("");
    try {
      const result = await postJson<AdaptiveRefreshResponse>("/api/strategy/adaptive/refresh", {});
      setMessage(result.status === "queued"
        ? "30일/7일 OOS 전략 재검증을 시작했습니다. 완료 전까지 기존 정책을 유지합니다."
        : "전략 재검증이 이미 실행 중입니다.");
      await client.invalidateQueries({ queryKey: ["adaptive-strategy"] });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "전략 재검증을 시작하지 못했습니다.");
    } finally {
      setRefreshing(false);
    }
  }

  return <div className="page-stack">
    <div className="page-intro"><div><span className="eyebrow">ADAPTIVE STRATEGY</span><h2>전략이 실제로 벌었는지부터 확인합니다</h2><p>실거래 closed batch의 실제 체결 수수료를 우선 반영하고, 과거 수수료가 부족한 경우 보수적으로 추정해 전략·시장·국면별 성과를 분리합니다.</p></div></div>

    <section className="panel">
      <div className="panel__head"><div><span className="eyebrow">LIVE ATTRIBUTION · {performanceWindow.toUpperCase()}</span><h2>전략별 실거래 성과</h2></div><div className="performance-head-actions"><div className="window-tabs"><button className={performanceWindow === "7d" ? "active" : ""} onClick={() => setPerformanceWindow("7d")}>7일</button><button className={performanceWindow === "30d" ? "active" : ""} onClick={() => setPerformanceWindow("30d")}>30일</button><button className={performanceWindow === "lifetime" ? "active" : ""} onClick={() => setPerformanceWindow("lifetime")}>전체</button></div><div className="panel-note">최근 집계 {dateTime(performanceQuery.data?.generated_at_utc || null)}</div></div></div>
      {performanceQuery.isLoading ? <div className="skeleton" /> : performanceQuery.isError ? <div className="empty-state"><strong>실거래 성과를 불러오지 못했습니다</strong><p>{performanceQuery.error instanceof Error ? performanceQuery.error.message : "성과 집계 API를 확인해 주세요."}</p></div> : liveStrategies.length ? <div className="responsive-table"><table><thead><tr><th>전략</th><th>거래</th><th>승률</th><th>Net PnL</th><th>PF</th><th>기대값/거래</th><th>평균 수익률</th><th>수수료</th><th>최대 연속손실</th></tr></thead><tbody>{liveStrategies.map((row) => <tr key={row.strategy}><td><strong>{strategyTitle(row.strategy)}</strong></td><td>{row.trade_count}회</td><td>{pct(row.win_rate_pct, 1)}</td><td className={row.net_pnl_krw >= 0 ? "tone-positive" : "tone-negative"}>{money(row.net_pnl_krw)}</td><td>{pf(row.profit_factor)}</td><td className={row.expectancy_krw >= 0 ? "tone-positive" : "tone-negative"}>{money(row.expectancy_krw)}</td><td>{pct(row.avg_return_pct)}</td><td>{Math.round(row.fees_krw).toLocaleString("ko-KR")}원</td><td>{row.max_consecutive_losses}회</td></tr>)}</tbody></table></div> : <div className="empty-state"><strong>아직 집계 가능한 자동전략 실거래가 없습니다</strong><p>manual 거래와 미종료 포지션은 성과 표본에서 제외됩니다.</p></div>}
    </section>

    <section className="panel">
      <div className="panel__head"><div><span className="eyebrow">LIVE EXECUTION QUALITY · 30D</span><h2>백테스트 비용 가정과 실제 체결 차이</h2></div><div className="panel-note">Adaptive 기준: {executionQualityQuery.data?.adaptive_friction.observed_source === "live_p75" ? "실측 p75" : "설정값 fallback"}</div></div>
      {executionQualityQuery.isLoading ? <div className="skeleton" /> : executionQualityQuery.isError || !executionQualityQuery.data ? <div className="empty-state"><strong>체결품질 데이터를 불러오지 못했습니다</strong><p>신규 주문부터 order UUID 기준으로 실측 데이터가 쌓입니다.</p></div> : (() => {
        const quality = executionQualityQuery.data;
        const rows = [["매수", quality.windows["30d"].bid], ["매도", quality.windows["30d"].ask]] as const;
        return <><div className="execution-friction-strip"><div><span>설정 총 friction</span><strong>{quality.adaptive_friction.configured_total_bps.toFixed(1)} bps</strong></div><div><span>실측 적용값</span><strong>{quality.adaptive_friction.observed_total_bps.toFixed(1)} bps</strong></div><div><span>Stress</span><strong>{quality.adaptive_friction.stress_total_bps.toFixed(1)} bps</strong></div><div><span>완료 표본</span><strong>{quality.adaptive_friction.sample_count} / {quality.adaptive_friction.min_samples}</strong></div></div><div className="responsive-table"><table><thead><tr><th>방향</th><th>표본</th><th>Median</th><th>P75</th><th>P90</th><th>첫 체결 Median</th><th>Terminal P90</th><th>실제 수수료</th></tr></thead><tbody>{rows.map(([label, metric]) => <tr key={label}><td><strong>{label}</strong></td><td>{metric.sample_count}</td><td>{metric.median_adverse_friction_bps.toFixed(1)} bps</td><td>{metric.p75_adverse_friction_bps.toFixed(1)} bps</td><td>{metric.p90_adverse_friction_bps.toFixed(1)} bps</td><td>{Math.round(metric.median_first_fill_ms)} ms</td><td>{Math.round(metric.p90_terminal_fill_ms)} ms</td><td>{Math.round(metric.total_actual_fee_krw).toLocaleString("ko-KR")}원</td></tr>)}</tbody></table></div></>;
      })()}
    </section>

    <div className="performance-grid">
      <section className="panel">
        <div className="panel__head"><div><span className="eyebrow">REGIME ATTRIBUTION · {performanceWindow.toUpperCase()}</span><h2>시장 국면별 성과</h2></div></div>
        {regimeRows.length ? <div className="responsive-table"><table><thead><tr><th>전략</th><th>국면</th><th>거래</th><th>Net PnL</th><th>PF</th><th>기대값</th></tr></thead><tbody>{regimeRows.map((row) => <tr key={`${row.strategy}-${row.regime}`}><td>{strategyTitle(row.strategy)}</td><td>{row.regime || "unknown"}</td><td>{row.trade_count}</td><td className={row.net_pnl_krw >= 0 ? "tone-positive" : "tone-negative"}>{money(row.net_pnl_krw)}</td><td>{pf(row.profit_factor)}</td><td>{money(row.expectancy_krw)}</td></tr>)}</tbody></table></div> : <div className="empty-state"><strong>국면별 표본이 없습니다</strong><p>진입 시그널의 market regime이 연결된 closed batch가 쌓이면 표시됩니다.</p></div>}
      </section>
      <section className="panel">
        <div className="panel__head"><div><span className="eyebrow">MARKET ATTRIBUTION · {performanceWindow.toUpperCase()}</span><h2>시장별 주요 성과</h2></div></div>
        {marketRows.length ? <div className="responsive-table"><table><thead><tr><th>마켓</th><th>전략</th><th>거래</th><th>Net PnL</th><th>PF</th><th>보유</th></tr></thead><tbody>{marketRows.map((row) => <tr key={`${row.strategy}-${row.market}`}><td><strong>{row.market}</strong></td><td>{strategyTitle(row.strategy)}</td><td>{row.trade_count}</td><td className={row.net_pnl_krw >= 0 ? "tone-positive" : "tone-negative"}>{money(row.net_pnl_krw)}</td><td>{pf(row.profit_factor)}</td><td>{minutes(row.avg_hold_minutes)}</td></tr>)}</tbody></table></div> : <div className="empty-state"><strong>시장별 표본이 없습니다</strong><p>자동전략 실현 거래가 생성되면 거래가 많은 시장부터 표시됩니다.</p></div>}
      </section>
    </div>

    <section className="strategy-overview">
      <div className="strategy-status"><div className={`strategy-status__lamp ${data.enabled ? "on" : "off"}`}><Activity size={22} /></div><div><span>Adaptive Engine</span><strong>{data.enabled ? "활성" : "비활성"}</strong><p>{data.analysis_market_count}개 시장을 major·유동성·변동성·상승/하락 모멘텀으로 분산 분석합니다.</p></div></div>
      <div className="strategy-meta"><div><Clock3 size={16} /><span>다음 평가</span><strong>{dateTime(data.next_evaluation_at_utc)}</strong></div><div><RefreshCw size={16} /><span>최근 정책</span><strong>{data.refresh_running ? "재검증 실행 중" : dateTime(data.generated_at_utc)}</strong></div><div><Gauge size={16} /><span>전환 쿨다운</span><strong>{data.switch_cooldown_hours}시간</strong></div></div>
    </section>

    <section className="strategy-cards">
      {strategies.length ? strategies.map(([name, metric]) => {
        const label = LABELS[name] || { title: name, subtitle: "Adaptive strategy" };
        return <article className={`strategy-card ${metric.enabled ? "enabled" : "disabled"}`} key={name}>
          <header><div><span className="eyebrow">{label.subtitle}</span><h3>{label.title}</h3></div><span className={`strategy-switch ${metric.enabled ? "on" : "off"}`}>{metric.enabled ? "ON" : "OFF"}</span></header>
          <div className="strategy-metrics"><div><span>30일 수익</span><strong className={metric.return_30d_pct >= 0 ? "tone-positive" : "tone-negative"}>{pct(metric.return_30d_pct)}</strong></div><div><span>7일 수익</span><strong className={metric.return_7d_pct >= 0 ? "tone-positive" : "tone-negative"}>{pct(metric.return_7d_pct)}</strong></div><div><span>30일 PF</span><strong>{pf(metric.profit_factor_30d)}</strong></div><div><span>30일 기대값</span><strong className={metric.expectancy_30d_krw >= 0 ? "tone-positive" : "tone-negative"}>{money(metric.expectancy_30d_krw)}</strong></div><div><span>중앙 거래수익</span><strong>{pct(metric.median_trade_return_30d_pct)}</strong></div><div><span>Fee drag</span><strong>{pct(metric.fee_ratio_30d_pct, 1)}</strong></div><div><span>OOS 수익</span><strong className={metric.oos_return_pct >= 0 ? "tone-positive" : "tone-negative"}>{pct(metric.oos_return_pct)}</strong></div><div><span>OOS PF</span><strong>{pf(metric.oos_profit_factor)}</strong></div><div><span>OOS 기대값</span><strong className={metric.oos_expectancy_krw >= 0 ? "tone-positive" : "tone-negative"}>{money(metric.oos_expectancy_krw)}</strong></div><div><span>OOS MDD</span><strong>{pct(metric.oos_max_drawdown_pct)}</strong></div><div><span>OOS 폴드</span><strong>{metric.oos_fold_count}개 · {pct(metric.oos_profitable_fold_pct, 0)}</strong></div><div><span>30일 MDD</span><strong>{pct(metric.avg_mdd_30d_pct)}</strong></div><div><span>거래</span><strong>{metric.trade_count_30d}회</strong></div><div><span>OOS 거래</span><strong>{metric.oos_trade_count}회</strong></div></div>
          <div className="strategy-reason"><Target size={16} /><span>{metric.reason || "평가 사유 없음"}</span></div>
          {metric.score_bonus !== 0 && <div className="strategy-bonus"><TrendingUp size={15} />Router 보정 {metric.score_bonus > 0 ? "+" : ""}{metric.score_bonus.toFixed(1)}</div>}
        </article>;
      }) : <div className="empty-state"><strong>첫 30일 정책 분석을 기다리는 중</strong><p>서비스는 켜져 있어도 자동매매가 OFF이면 실제 주문은 발생하지 않습니다.</p></div>}
    </section>

    <section className="panel">
      <div className="panel__head"><div><span className="eyebrow">REGIME × STRATEGY GATE</span><h2>국면별 신규진입 허용 매트릭스</h2><p className="panel-copy">전역 검증을 통과한 전략도 해당 국면의 30일 PF·기대값과 OOS PF·기대값·MDD 표본을 다시 통과해야 ON이 됩니다.</p></div></div>
      {Object.keys(data.regime_strategies || {}).length ? <div className="responsive-table"><table><thead><tr><th>전략</th>{REGIMES.map(([key, label]) => <th key={key}>{label}</th>)}</tr></thead><tbody>{Object.keys(data.strategies || {}).map((name) => <tr key={name}><td><strong>{strategyTitle(name)}</strong></td>{REGIMES.map(([regime]) => {
        const metric = data.regime_strategies?.[regime]?.[name];
        return <td key={`${name}-${regime}`}>{metric ? <div className={`regime-policy-cell ${metric.enabled ? "on" : "off"}`} title={metric.reason}><span>{metric.enabled ? "ON" : "OFF"}</span><strong>PF {pf(metric.profit_factor_30d)}</strong><small>{metric.trade_count_30d}회 · Exp {money(metric.expectancy_30d_krw)}</small><small>OOS PF {pf(metric.oos_profit_factor)} · {metric.oos_trade_count}회</small></div> : <div className="regime-policy-cell off"><span>OFF</span><small>검증 데이터 없음</small></div>}</td>;
      })}</tr>)}</tbody></table></div> : <div className="empty-state"><strong>국면별 Adaptive 검증을 기다리는 중</strong><p>4차 정책 연구가 완료되기 전에는 regime별 신규 enable을 허용하지 않습니다.</p></div>}
    </section>

    <section className="adaptive-settings">
      <div><span className="eyebrow">RE-EVALUATION CADENCE</span><h3>전략 재평가 주기</h3><p>기본은 48시간입니다. 24시간은 더 민감하지만 구간 노이즈로 전략이 자주 바뀔 수 있습니다.</p></div>
      <div className="cadence-control"><button className={`cadence-button ${selectedInterval === 24 ? "active" : ""}`} onClick={() => setInterval(24)}>24시간<small>민감</small></button><button className={`cadence-button ${selectedInterval === 48 ? "active" : ""}`} onClick={() => setInterval(48)}>48시간<small>권장</small></button><button className="button primary" disabled={saving || selectedInterval === data.analysis_interval_hours} onClick={saveInterval}>{saving ? "저장 중…" : "주기 저장"}</button><button className="button" disabled={refreshing || data.refresh_running} onClick={refreshAdaptivePolicy}><RefreshCw size={16} />{refreshing || data.refresh_running ? "재검증 실행 중…" : "지금 재검증"}</button></div>
      {message && <div className="setting-message">{message}</div>}
    </section>

    <section className="adaptive-settings">
      <div><span className="eyebrow">LIVE SCAN POOL</span><h3>실시간 분석 풀</h3><p>급등 종목이 이 풀에 들어와야 점수를 받고 진입 후보가 됩니다. 상승 슬롯이 좁으면 급등이 관측되지 않습니다.</p></div>
      <div className="cadence-control">
        <label className="pool-field"><span>분석 종목 수</span><input type="number" min={5} max={200} value={selectedPoolLimit} onChange={(event) => setPoolLimit(Number(event.target.value))} /></label>
        <label className="pool-field"><span>상승 슬롯</span><input type="number" min={0} max={200} value={selectedMoverSlots} onChange={(event) => setMoverSlots(Number(event.target.value))} /></label>
        <button className="button primary" disabled={savingPool || !poolDirty} onClick={saveAnalysisPool}>{savingPool ? "저장 중…" : "분석 풀 저장"}</button>
      </div>
    </section>

    <div className="market-chip-row"><span>최근 분석 시장</span><div>{data.selected_markets.map((market) => <span className="market-chip" key={market}>{market}<small>{data.selected_market_buckets?.[market] || "legacy"}</small></span>)}</div></div>
  </div>;
}
